// Keeping chat and names kind: slurs and hateful words are never shown, and crude words are masked. The server
// filters every chat line and refuses names that carry one; the client uses the same rules to warn early.
//
// The word lists are stored ROT13-encoded so the source isn't a wall of slurs; they're decoded once at load.
// Matching looks through common disguises: capitals, l33t letters (0→o, 1→i, 3→e, 4→a, 5→s, 7→t, @→a, $→s),
// repeated letters ("fuuuck"), and letters split up by spaces or dots ("n . i . g").

const rot13 = (s: string) => s.replace(/[a-z]/g, c => String.fromCharCode((c.charCodeAt(0) - 97 + 13) % 26 + 97));
const dec = (list: string[]) => list.map(rot13);

/** Hateful words: always hidden. */
const SEVERE = dec(['avttre', 'avttn', 'avttnu', 'avtt', 'puvax', 'tbbx', 'fcvp', 'fcvpx', 'jrgonpx', 'xvxr', 'xlxr', 'enturnq', 'gbjryurnq', 'fnaqavttre', 'pbba', 'wvtnobb',
  'cbepuzbaxrl', 'ornare', 'cnxv', 'genaal', 'snttbg', 'sntbg', 'snt', 'snttl', 'qlxr', 'ergneq', 'ergneqrq', 'anmv', 'uvgyre', 'xxx', 'juvgrcbjre', 'urvyuvgyre', 'arteb', 'furznyr',
  'puvanzna', 'wnc', 'tbyyvjbt', 'qnexvr', 'mvccreurnq', 'vawha', 'erqfxva', 'unysoerrq', 'jbc', 'qntb', 'cbynpx', 'ubaxl', 'fynagrlr']);
/** Of those, the ones found even inside other letters (nothing innocent contains them). */
const INSIDE = new Set(dec(['avttn', 'avttnu', 'avttre', 'cbepuzbaxrl', 'enturnq', 'fnaqavttre', 'fynagrlr', 'gbjryurnq', 'genaal', 'jrgonpx', 'juvgrcbjre', 'mvccreurnq', 'puvax',
  'snttbg', 'tbyyvjbt', 'urvyuvgyre', 'wvtnobb', 'xvxr']));
/** Crude words: masked in chat, refused in names. Stems match the start of a word ("…ing", "…er"). */
const STEMS = dec(['shpx', 'shx', 'spx', 'fuvg', 'ovgpu', 'phag', 'zbgureshpxre', 'pbpxfhpx', 'wnax', 'jnax', 'cbea', 'crqbcuvy', 'zbyrfg', 'encr', 'encvfg', 'qvyqb', 'vaprfg']);
const WORDS = new Set(dec(['nffubyr', 'nefrubyr', 'qvpx', 'qvpxurnq', 'chffl', 'onfgneq', 'juber', 'fyhg', 'pbpx', 'gjng', 'cevpx', 'obyybpxf', 'wvmm', 'phz', 'cravf', 'intvan', 'obbo',
  'obbof', 'gvgf', 'gvggl', 'ahqrf', 'ubeal', 'zvys', 'crqb', 'xlf', 'xvyylbhefrys', 'fgsh', 'nff']));

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '!': 'i', '|': 'i', '3': 'e', '4': 'a', '@': 'a', '5': 's', '$': 's', '7': 't', '+': 't', '8': 'b', '9': 'g' };
/** One letter per original character (or '' for characters that aren't letters), lowercased and un-l33ted. */
function letters(s: string): string[] {
  return [...s].map(ch => { const c = ch.toLowerCase(); const l = LEET[c] ?? c; return /^[a-z]$/.test(l) ? l : ''; });
}
/** Squash runs of the same letter ("fuuuck" → "fuck"), keeping a map back to where each letter came from. */
function squash(ls: { c: string; i: number }[]): { c: string; i: number[] }[] {
  const out: { c: string; i: number[] }[] = [];
  for (const l of ls) { const last = out[out.length - 1]; if (last && last.c === l.c) last.i.push(l.i); else out.push({ c: l.c, i: [l.i] }); }
  return out;
}
const sq = (w: string) => w.replace(/(.)\1+/g, '$1');
/** Innocent words that start like a crude one. */
const ALLOW = new Set(['shitake', 'shitakes', 'wankel', 'raped'].slice(0, 2).map(w => w.replace(/(.)\1+/g, '$1')));
const SEVERE_SQ = SEVERE.map(w => ({ w: sq(w), inside: INSIDE.has(w) }));
const STEMS_SQ = STEMS.map(sq), WORDS_SQ = new Set([...WORDS].map(sq));

/** Which characters of `text` belong to a bad word (true = hide). */
function flags(text: string): boolean[] {
  const chars = [...text], L = letters(text), hide = new Array<boolean>(chars.length).fill(false);
  // words: runs of letters; a run of single letters split by spaces or dots counts as one word too
  const words: { c: string; i: number }[][] = [];
  let cur: { c: string; i: number }[] = [];
  const flush = () => { if (cur.length) words.push(cur); cur = []; };
  chars.forEach((ch, i) => { if (L[i]) cur.push({ c: L[i], i }); else if (/\s/.test(ch) || /[^\p{L}\p{N}]/u.test(ch)) flush(); });
  flush();
  const spaced: { c: string; i: number }[][] = [];
  for (let k = 0; k < words.length;) {   // "n i g g e r", "s.h.i.t"
    if (words[k].length === 1) { let j = k; const run: { c: string; i: number }[] = []; while (j < words.length && words[j].length === 1) run.push(words[j++][0]); if (run.length >= 3) spaced.push(run); k = j; } else k++;
  }
  const mark = (w: { c: string; i: number[] }[], from: number, len: number) => { for (let k = from; k < from + len; k++) for (const i of w[k].i) hide[i] = true; };
  for (const raw of [...words, ...spaced]) {
    const w = squash(raw), s = w.map(x => x.c).join('');
    let whole = false;
    for (const { w: bad, inside } of SEVERE_SQ) {
      if (inside) { if (s.includes(bad)) whole = true; }
      else if (s === bad || s === bad + 's' || s === bad + 'es' || (bad.length >= 5 && s.startsWith(bad) && /^(ed|ing|ers?)$/.test(s.slice(bad.length)))) whole = true;
    }
    if (!whole && !ALLOW.has(s)) for (const st of STEMS_SQ) if (s.startsWith(st)) { whole = true; break; }
    if (!whole && (WORDS_SQ.has(s) || (s.endsWith('s') && WORDS_SQ.has(s.slice(0, -1))) || (s.endsWith('es') && WORDS_SQ.has(s.slice(0, -2))))) whole = true;
    if (whole) mark(w, 0, w.length);
  }
  return hide;
}

/** A chat line with bad words masked, and whether anything was. */
export function filterChat(text: string): { text: string; masked: boolean } {
  const hide = flags(text);
  if (!hide.some(Boolean)) return { text, masked: false };
  const chars = [...text];
  return { text: chars.map((ch, i) => hide[i] ? '•' : ch).join(''), masked: true };
}

/** Would this name be refused? Names have no spaces: hateful words are looked for anywhere in them (the ones
 *  nothing innocent contains) or as the whole name, give or take numbers; crude words as the start or the whole. */
export function nameBlocked(name: string): boolean {
  const s = sq(letters(name.replace(/\d+$/, '')).join(''));
  if (!s) return false;
  if (SEVERE_SQ.some(({ w, inside }) => inside ? s.includes(w) : s === w || s === w + 's')) return true;
  if (s.includes(sq(rot13('shpx')))) return true;
  if (STEMS_SQ.some(st => st.length >= 4 && s.startsWith(st))) return true;
  return WORDS_SQ.has(s) || (s.endsWith('s') && WORDS_SQ.has(s.slice(0, -1)));
}

// ---------- gil sellers ----------
// Their adverts look alike: a website, a price, a payment method, "cheap gil", "buy gold". Letters spaced out or
// swapped for numbers are caught the same way as for rude words.
const SELLER = [/\b(buy|cheap|sell(ing)?|fast|safe|legit)\W{0,3}(gil|gold|g)\b/, /\b(gil|gold)\W{0,3}(for|4)\W{0,3}(sale|cheap|usd|\$|cash|money)/, /\b(usd|paypal|venmo|cashapp|btc|bitcoin|crypto|wechat|skype)\b/,
  /(\$|€|£)\s?\d|\d\s?(\$|€|£|usd|bucks)\b/, /\b(www|https?)\b|\b[a-z0-9-]{2,}\s?\.\s?(com|net|org|gg|io|co|xyz|shop|store|cc|ru|top)\b/, /discord\s?\.?\s?(gg|com\/invite)/,
  /\b(power\s?level(ing)?|leveling service|account(s)? for sale|boosting)\b/];
/** Does this message look like a gil seller's advert? */
export function sellerSpam(text: string): boolean {
  const t = text.toLowerCase().replace(/[0@]/g, 'o').replace(/[1!|]/g, 'i').replace(/3/g, 'e').replace(/5/g, 's').replace(/\s+/g, ' ');
  const squeezed = t.replace(/(?<=\b\w) (?=\w\b)/g, '');   // "c h e a p g i l" -> "cheapgil"
  return SELLER.some(r => r.test(t) || r.test(squeezed)) || /cheapgil|buygil|buygold|gilforsale/.test(squeezed.replace(/\W/g, ''));
}
